import { useEffect, useState, useMemo } from "react";
import { UserPlus, Mail, KeyRound, Pencil, Trash2, Search, Crown, ShieldCheck, User as UserIcon, Lock, ShieldOff, ShieldAlert, Users, ShoppingBag, Phone, MapPin, Clock } from "lucide-react";
import { eur } from "@/lib/api";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_LABELS = { superadmin: "Super admin", admin: "Admin", customer: "Client" };
const ROLE_STYLES = {
  superadmin: "bg-gold/15 text-copper border-gold/40",
  admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
  customer: "bg-cream text-graphite border-stone",
};
const Lbl = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>
);
const EMPTY = { email: "", name: "", password: "", role: "admin" };
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso.slice(0, 10); }
};
const fmtDateTime = (iso) => {
  if (!iso) return "jamais";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso.slice(0, 16); }
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ total: 0, staff: 0, superadmin: 0, admin: 0, customer: 0, customer_blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Deux populations distinctes : l'équipe de la plateforme (accès back-office)
  // et les clients de la boutique (inscrits via le site).
  const [tab, setTab] = useState("staff"); // "staff" | "customers"
  const [roleFilter, setRoleFilter] = useState("all"); // sous-filtre de l'équipe

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "admin", active: true });
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState("");

  const load = () => {
    setLoading(true);
    const params = { kind: tab };
    if (tab === "staff" && roleFilter !== "all") params.role = roleFilter;
    if (search) params.search = search;
    api.get("/superadmin/users", { params })
      .then(({ data }) => { setUsers(data.users); setCounts(data.counts); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, tab]);

  const initials = useMemo(() => (name, email) => {
    const src = (name || email || "?").trim();
    return src.slice(0, 2).toUpperCase();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name || !createForm.password) {
      toast.error("Email, nom et mot de passe requis");
      return;
    }
    setSaving(true);
    try {
      await api.post("/superadmin/users", createForm);
      toast.success("Utilisateur créé");
      setCreateOpen(false);
      setCreateForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", role: u.role, active: u.active !== false });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/superadmin/users/${editing.id}`, editForm);
      toast.success("Utilisateur mis à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/superadmin/users/${u.id}/reset-password`);
      if (data.sent) toast.success(`Email de réinitialisation envoyé à ${u.email}`);
      else toast.warning("Email non envoyé — vérifiez la configuration Resend");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwdValue.length < 6) {
      toast.error("6 caractères minimum");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/superadmin/users/${pwdTarget.id}/set-password`, { password: pwdValue });
      toast.success("Mot de passe défini");
      setPwdTarget(null);
      setPwdValue("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/superadmin/users/${toDelete.id}`);
      toast.success("Utilisateur supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmBlock = async () => {
    if (!blockTarget) return;
    setBusyId(blockTarget.id);
    try {
      await api.patch(`/superadmin/users/${blockTarget.id}/block`, { reason: blockReason || null });
      toast.success("Compte bloqué");
      setBlockTarget(null);
      setBlockReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const unblockUser = async (u) => {
    setBusyId(u.id);
    try {
      await api.patch(`/superadmin/users/${u.id}/unblock`);
      toast.success("Compte débloqué");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Administration</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-graphite mt-2">
            Deux populations distinctes : <strong className="text-ink">l'équipe de la plateforme</strong> (super admins et admins, accès au back-office)
            et <strong className="text-ink">les clients de la boutique</strong> (comptes créés via le site pour commander).
          </p>
        </div>
        <button data-testid="add-user-btn" onClick={() => { setCreateForm({ ...EMPTY, role: tab === "customers" ? "customer" : "admin" }); setCreateOpen(true); }} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
          <UserPlus className="h-4 w-4" /> {tab === "customers" ? "Ajouter un client" : "Ajouter un membre"}
        </button>
      </div>

      {/* Onglets : équipe plateforme / clients boutique */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          data-testid="tab-staff"
          onClick={() => setTab("staff")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.12em] rounded-sm transition-colors ${tab === "staff" ? "bg-ink text-cream" : "bg-white border border-stone text-graphite hover:border-graphite"}`}
        >
          <ShieldCheck className="h-3.5 w-3.5" /> Équipe plateforme
          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${tab === "staff" ? "bg-cream text-ink" : "bg-sand text-graphite"}`}>{counts.staff}</span>
        </button>
        <button
          data-testid="tab-customers"
          onClick={() => setTab("customers")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.12em] rounded-sm transition-colors ${tab === "customers" ? "bg-ink text-cream" : "bg-white border border-stone text-graphite hover:border-graphite"}`}
        >
          <ShoppingBag className="h-3.5 w-3.5" /> Clients de la boutique
          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${tab === "customers" ? "bg-cream text-ink" : "bg-sand text-graphite"}`}>{counts.customer}</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {(tab === "staff"
          ? [
              { label: "Équipe plateforme", value: counts.staff, icon: Users },
              { label: "Super admins", value: counts.superadmin, icon: ShieldCheck },
              { label: "Admins", value: counts.admin, icon: Lock },
              { label: "Tous les comptes", value: counts.total, icon: UserIcon },
            ]
          : [
              { label: "Clients inscrits", value: counts.customer, icon: ShoppingBag },
              { label: "Clients bloqués", value: counts.customer_blocked, icon: ShieldOff },
              { label: "Affichés", value: users.length, icon: Search },
              { label: "Tous les comptes", value: counts.total, icon: UserIcon },
            ]
        ).map((s, i) => (
          <div key={i} className="bg-white border border-stone rounded-sm p-4">
            <p className="text-[10px] uppercase tracking-[0.15em] text-ash flex items-center gap-1.5"><s.icon className="h-3 w-3" /> {s.label}</p>
            <p className="font-mono text-2xl mt-1 text-ink">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-ash absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "customers" ? "Rechercher un client (email, nom, téléphone)..." : "Rechercher un membre (email, nom)..."} className="pl-9 rounded-sm border-stone bg-white" />
        </div>
        {tab === "staff" && (
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="user-role-filter" className="w-full md:w-48 rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="superadmin">Super admins</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="bg-white border border-stone rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-12 bg-sand animate-pulse" />)}</div>
        ) : users.length === 0 ? (
          <p data-testid="no-users" className="p-10 text-center text-graphite">{tab === "customers" ? "Aucun client." : "Aucun membre de l'équipe."}</p>
        ) : (
          <div className="divide-y divide-stone">
            {users.map((u) => {
              const isSelf = me && me.id === u.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.email}`} className={`flex items-center gap-4 p-4 hover:bg-sand/50 ${u.active === false ? "opacity-60" : ""} ${u.is_blocked ? "bg-red-50/30" : ""}`}>
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${u.is_root ? "bg-gold text-ink" : u.is_blocked ? "bg-red-100 text-red-600" : "bg-stone text-graphite"}`}>
                    {initials(u.name, u.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-ink truncate">{u.name || "—"}</p>
                      {u.is_root && (
                        <span data-testid={`root-badge-${u.email}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm bg-gold/15 text-copper border border-gold/40">
                          <Crown className="h-3 w-3" /> Compte racine protégé
                        </span>
                      )}
                      {isSelf && <span className="text-[10px] uppercase tracking-[0.1em] text-ash">(vous)</span>}
                    </div>
                    <p className="text-xs text-ash truncate">{u.email}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border ${ROLE_STYLES[u.role]}`}>
                        {u.role === "superadmin" ? <ShieldCheck className="h-3 w-3" /> : u.role === "admin" ? <Lock className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                      {u.is_blocked ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border bg-red-50 text-red-700 border-red-200" title={u.block_reason || undefined}>
                          <ShieldOff className="h-3 w-3" /> Bloqué{u.blocked_at ? ` le ${fmtDate(u.blocked_at)}` : ""}
                        </span>
                      ) : (
                        <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border ${u.active === false ? "bg-red-50 text-destructive border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {u.active === false ? "Désactivé" : "Actif"}
                        </span>
                      )}
                      {u.role === "customer" && (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border ${u.orders_count > 0 ? "bg-gold/10 text-copper border-gold/30" : "bg-cream text-ash border-stone"}`}>
                          <ShoppingBag className="h-3 w-3" /> {u.orders_count > 0 ? `${u.orders_count} commande${u.orders_count > 1 ? "s" : ""} · ${eur(u.total_spent || 0)}` : "aucune commande"}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] text-ash">
                      {u.role === "customer" && u.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {u.phone}</span>}
                      {u.role === "customer" && (u.city || u.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[u.city, u.country].filter(Boolean).join(", ")}</span>}
                      <span className="inline-flex items-center gap-1" title="Date de création du compte">{u.role === "customer" ? "Inscrit le" : "Créé le"} {fmtDate(u.created_at)}</span>
                      <span className="inline-flex items-center gap-1" title={u.last_login_ip ? `Dernière IP : ${u.last_login_ip}` : undefined}><Clock className="h-3 w-3" /> Dernière connexion : {fmtDateTime(u.last_login)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                    <button data-testid={`reset-pwd-${u.email}`} disabled={busyId === u.id} onClick={() => sendReset(u)} title="Envoyer un email de réinitialisation" className="p-2 text-graphite hover:text-copper disabled:opacity-40">
                      <Mail className="h-4 w-4" />
                    </button>
                    {!u.is_root && (
                      <>
                        <button data-testid={`set-pwd-${u.email}`} onClick={() => { setPwdTarget(u); setPwdValue(""); }} title="Définir un mot de passe" className="p-2 text-graphite hover:text-ink">
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)} title="Modifier" className="p-2 text-graphite hover:text-ink">
                          <Pencil className="h-4 w-4" />
                        </button>
                        {(u.role === "customer" || u.role === "admin") && (
                          u.is_blocked ? (
                            <button
                              disabled={busyId === u.id}
                              onClick={() => unblockUser(u)}
                              title="Débloquer ce compte"
                              className="p-2 text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
                            >
                              <ShieldAlert className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              disabled={busyId === u.id}
                              onClick={() => { setBlockTarget(u); setBlockReason(""); }}
                              title="Bloquer ce compte"
                              className="p-2 text-graphite hover:text-red-600 disabled:opacity-40"
                            >
                              <ShieldOff className="h-4 w-4" />
                            </button>
                          )
                        )}
                        {!isSelf && (
                          <button data-testid={`delete-user-${u.email}`} onClick={() => setToDelete(u)} title="Supprimer" className="p-2 text-graphite hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-cream max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Nouveau compte</DialogTitle>
            <DialogDescription className="text-graphite text-sm">
              {tab === "customers"
                ? "Créez un compte client de la boutique (il pourra se connecter sur le site pour commander)."
                : "Créez un membre de l'équipe de la plateforme (accès au back-office)."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div><Lbl>Email</Lbl><Input data-testid="new-user-email" type="email" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <div><Lbl>Nom</Lbl><Input data-testid="new-user-name" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <div>
              <Lbl>Rôle</Lbl>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="new-user-role" className="mt-1.5 rounded-sm border-stone bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tab === "customers" ? (
                    <SelectItem value="customer">Client de la boutique</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="admin">Admin (équipe plateforme)</SelectItem>
                      <SelectItem value="superadmin">Super admin (équipe plateforme)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div><Lbl>Mot de passe (8 car. min. — 1 lettre + 1 chiffre)</Lbl><Input data-testid="new-user-password" type="text" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} minLength={8} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-user-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "Création..." : "Créer le compte"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Modifier {editing?.email}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div><Lbl>Nom</Lbl><Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <div>
              <Lbl>Rôle</Lbl>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="edit-user-role" className="mt-1.5 rounded-sm border-stone bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (équipe plateforme)</SelectItem>
                  <SelectItem value="superadmin">Super admin (équipe plateforme)</SelectItem>
                  <SelectItem value="customer">Client de la boutique</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border border-stone rounded-sm p-3">
              <div><Lbl>Compte actif</Lbl><p className="text-xs text-graphite mt-1">Un compte désactivé ne peut plus se connecter.</p></div>
              <Switch data-testid="edit-user-active" checked={editForm.active} onCheckedChange={(v) => setEditForm((f) => ({ ...f, active: v }))} />
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="update-user-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "Enregistrement..." : "Mettre à jour"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set password dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
        <DialogContent className="bg-cream max-w-md" data-testid="set-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Définir un mot de passe</DialogTitle>
            <DialogDescription className="text-graphite text-sm">Pour {pwdTarget?.email}. L&apos;utilisateur devra utiliser ce nouveau mot de passe.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPwd} className="space-y-4">
            <div><Lbl>Nouveau mot de passe (8 car. min. — 1 lettre + 1 chiffre)</Lbl><Input data-testid="set-pwd-value" type="text" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} minLength={8} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-pwd-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "..." : "Définir le mot de passe"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink">Supprimer ce compte ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.email} » sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-btn" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog confirmation blocage */}
      <AlertDialog open={!!blockTarget} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <AlertDialogContent className="bg-cream max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-red-600" />
              Bloquer ce compte ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-graphite text-sm">
              <strong>{blockTarget?.email}</strong> ne pourra plus se connecter et sera redirigé vers le support.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <label className="text-xs uppercase tracking-[0.12em] text-ash block mb-1.5">Motif (optionnel)</label>
            <input
              type="text"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Ex : suspicion de fraude, compte à vérifier…"
              className="w-full border border-stone rounded-sm px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-red-400"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBlock} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">
              Bloquer le compte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
